<template>
  <va-form
    :id="isEdit ? sidebar.id : ''"
    class="create-sidebar__form"
    ref="form"
    :item="sidebar"
    v-model="form"
    :before-save="beforeSave"
    :after-save="afterSave"
    :modify-before-send="modifyBeforeSend"
    :custom-redirect-route="
      !!this.$route.query.domainId
        ? {
            name: 'domains_show',
            params: { id: domainId },
            query: { tab: 'pages_add_ons' },
          }
        : {
            name: 'sidebars_list',
          }
    "
    :required-fields-set="requiredFieldsSet">
    <!-- Details -->
    <v-card class="spacing-bottom--24">
      <div class="d-flex">
        <div class="d-flex flex-column">
          <v-card-title>{{ $t("resources.sidebars.sidebar_details_block_title") }}</v-card-title>
          <v-card-subtitle class="spacing-bottom--16">{{
            $t("resources.sidebars.sidebar_details_block_subtitle")
          }}</v-card-subtitle>
        </div>
        <v-card-actions class="ml-auto">
          <v-checkbox
            color="primary"
            v-model="form.isDefault"
            :label="$t('resources.sidebars.sidebar_default')"></v-checkbox>
        </v-card-actions>
      </div>
      <va-text-input
        v-model="form.name"
        required
        source="name"
        class="spacing-bottom--16"
        :label="$t('resources.sidebars.sidebar_name')"
        :placeholder="$t('resources.sidebars.sidebar_name_placeholder')"></va-text-input>

      <va-autocomplete-input
        v-model="form.domain"
        source="domain"
        :disabled="isDomainProvided"
        required
        :placeholder="$t('resources.sidebars.sidebar_domain_placeholder')"
        :label="$t('resources.sidebars.sidebar_domain')"
        :choices="domainsList"
        :min-chars="1"
        cache-items
        @change="domainChanged"></va-autocomplete-input>

      <div class="page-wrapper" v-if="form.pages && form.pages.length > 0">
        <div class="page-label">
          {{ $t("resources.sidebars.fields.page") }}
        </div>
        <div class="chip-container">
          <v-chip
            color="primary-20"
            text-color="secondary-btn"
            close
            close-icon="mdi-close"
            @click:close="removePage(page)"
            v-for="page in getPageArray"
            :key="page.id">
            {{ getPageName(page) }}
          </v-chip>
          <div v-if="form.pages.length > 12">
            <v-btn
              v-if="!showAllPageChips"
              text
              color="primary"
              class="chip-action-button"
              @click="showAllPageChips = true"
              :ripple="false">
              {{ $t("resources.calendars.show_all") }}
            </v-btn>
            <v-btn
              v-else
              text
              color="primary"
              class="chip-action-button"
              @click="showAllPageChips = false"
              :ripple="false">
              {{ $t("resources.calendars.collapse") }}
            </v-btn>
          </div>
        </div>
      </div>
    </v-card>
    <v-card class="spacing-bottom--16">
      <v-card-title>{{ $t("resources.sidebars.sidebar_items_block_title") }}</v-card-title>
      <v-card-subtitle class="spacing-bottom--11">
        {{ $t("resources.sidebars.sidebar_items_block_subtitle") }}
      </v-card-subtitle>
      <va-array-input
        v-model="form.menu.menuItems"
        v-slot="props"
        source="menu.menuItems"
        :label="false"
        :hide-details="'auto'"
        :add-button-text="$t('generic.add_menu_link')">
        <v-select
          v-model="form.menu.menuItems[props.index].type"
          persistent-placeholder
          :placeholder="$t('resources.sidebars.sidebar_menu_item_type_placeholder')"
          source="type"
          :hide-details="'auto'"
          :label="$t('resources.sidebars.menu_type')"
          :items="MENU_ITEM_TYPES"
          @change="changeMenuType(props.index)"
          :menu-props="{
            offsetOverflow: true,
            offsetY: true,
          }">
          <template v-slot:append>
            <v-icon>mdi-chevron-down</v-icon>
          </template>
        </v-select>
        <va-text-input
          v-model="form.menu.menuItems[props.index].text"
          class="spacing-horizontal--16"
          :label="$t('resources.sidebars.item_title')"
          :placeholder="$t('resources.sidebars.sidebar_menu_item_title_placeholder')"
          required
          v-bind="props"
          source="text"></va-text-input>
        <va-autocomplete-input
          v-if="form.menu.menuItems[props.index].type === 3"
          v-model="form.menu.menuItems[props.index].category"
          :label="$t('resources.pages.page_category')"
          source="category"
          :placeholder="$t('resources.sidebars.sidebar_menu_page_category_placeholder')"
          :filter="{ ...resourcesFilter }"
          required
          v-bind="props"
          reference="page_categories"
          item-value="@id"
          :min-chars="0"
          :items-per-page="5"
          showLoadMore>
        </va-autocomplete-input>
        <va-text-input
          v-if="form.menu.menuItems[props.index].type === 2"
          v-model="form.menu.menuItems[props.index].link"
          :label="$t('resources.sidebars.external_link')"
          :placeholder="$t('resources.sidebars.sidebar_menu_item_link_placeholder')"
          required
          :rules="[v => !!urlValidation(v) || $t('generic.validation.url')]"
          v-bind="props"
          source="link"></va-text-input>
        <va-autocomplete-input
          v-if="form.menu.menuItems[props.index].type !== 3 && form.menu.menuItems[props.index].type !== 2"
          v-model="form.menu.menuItems[props.index].page"
          v-bind="props"
          :label="$t('resources.sidebars.existing_page')"
          :placeholder="$t('generic.start_typing_to_search')"
          :filter="{ ...resourcesFilter, status: [1, 2] }"
          source="page"
          reference="pages"
          item-value="@id"
          required
          searchQuery="headline"
          :min-chars="0"
          :items-per-page="5"
          showLoadMore>
        </va-autocomplete-input>
        <v-select
          class="spacing-left--16"
          v-model="form.menu.menuItems[props.index].action"
          persistent-placeholder
          :menu-props="{ offsetY: true, offsetOverflow: true }"
          :hide-details="'auto'"
          :label="$t('generic.open_in')"
          :items="MENU_TAB_TYPES">
          <template v-slot:append>
            <v-icon>mdi-chevron-down</v-icon>
          </template>
        </v-select>
        <va-number-input
          style="display: none"
          disabled
          :value="(form.menu.menuItems[props.index].position = props.index + 1)"
          required
          v-bind="props"
          source="position"></va-number-input>
      </va-array-input>
    </v-card>
    <required-field-text></required-field-text>
  </va-form>
</template>

<script>
  import definedAbility from "@/ability";
  // Components
  import RequiredFieldText from "@/components/custom/RequiredFieldText";

  // Mixins
  import crudFormMixin from "@/mixins/crudFormMixin";
  import changeMenuType from "@/mixins/changeMenuTypeMethod";
  import ConnectedDomainsList from "@/mixins/connectedDomainsList";
  import providedDomain from "@/mixins/providedDomain";
  // Constants
  import { MENU_ITEM_TYPES, MENU_TAB_TYPES } from "../../constant/index";

  //Utils
  import { formatMenuItems } from "@/utility/formatMenuItems";
  import { urlValidation } from "@/utility/urlValidation";
  import { truncateText } from "@/utility/truncateText";

  export default {
    name: "SidebarForm",
    components: {
      RequiredFieldText,
    },
    mixins: [ConnectedDomainsList, changeMenuType, crudFormMixin, providedDomain],
    props: {
      sidebar: {
        type: Object,
        default: () => {},
      },
      // Determines if form is used while editing
      isEdit: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        urlValidation,
        ability: definedAbility(),
        MENU_ITEM_TYPES,
        MENU_TAB_TYPES,
        requiredFormFields: ["name", "domain"],
        form: {},
        oldDomain: "",
        oldPages: [],
        showAllPageChips: false,
      };
    },
    computed: {
      domainId() {
        return this.form.domain?.replace("/api/domains/", "");
      },
      resourcesFilter() {
        return this.form.domain ? { domain: this.form.domain.replace("/api/domains/", "") } : null;
      },
      getPageArray() {
        if (this.showAllPageChips) {
          return this.form.pages;
        } else {
          return this.form.pages.slice(0, 12);
        }
      },
    },
    beforeMount() {
      this.form = this.sidebar ? this.sidebar : {};
      if (!this.isEdit) {
        delete this.form["@context"];
        delete this.form["@id"];
        delete this.form["@type"];
      }
      this.form.domain = this.sidebar?.domain["@id"];
      this.form.template = this.sidebar?.template === "main" ? "default" : this.sidebar?.template;
      this.form.menu = this.sidebar?.menu || {};
      this.form.menu.domain = this.sidebar?.domain;
      this.form.menu.menuItems = formatMenuItems(this.sidebar?.menu?.menuItems);
      this.form.isDefault = this.sidebar?.isDefault || false;
      this.form.pages = this.sidebar?.pages || [];
      this.oldDomain = this.sidebar?.domain;
      this.oldPages = this.sidebar?.pages || [];
      //Check if it is not edit page and not create new page
      if (!this.isEdit && !!this.sidebar) {
        delete this.form.menu["@id"];
        delete this.form.menu.id;
        delete this.form.menu["@type"];

        //This conditional have to be here because menu could be without menuItems
        if (this.form.menu.menuItems) {
          this.form.menu.menuItems.map(item => {
            delete item["@id"];
            delete item["@type"];
          });
        }
      }
      if (!this.ability.can("view", "menu item page category")) {
        this.MENU_ITEM_TYPES = this.MENU_ITEM_TYPES.filter(x => !x.text.includes("Page Category"));
      }
    },
    methods: {
      getPageName(page) {
        return truncateText(page.headline);
      },
      removePage(page) {
        this.form.pages = this.form.pages.filter(item => item["@id"] !== page["@id"]);
        this.oldPages = this.form.pages;
      },
      modifyBeforeSend(data) {
        data.pages = data.pages.map(page => page["@id"]);

        return data;
      },
      domainChanged(domain) {
        if (this.form.menu) {
          this.form.menu.domain = domain;

          if (this.form.menu.menuItems) {
            this.form.menu.menuItems.forEach(item => {
              item.page = null;
              item.category = null;
            });
          }
        }
        if (this.oldDomain !== domain) {
          this.form.pages = [];
        } else {
          this.form.pages = this.oldPages;
        }
      },
      submit() {
        this.$refs.form.onSubmit();
      },
    },
    watch: {
      "form.menu.menuItems": {
        handler: function (newVal) {
          newVal.map(item => {
            if (!item.action) {
              item.action = MENU_TAB_TYPES[0].value;
            }
          });
        },
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../@core/preset/preset/functions.scss";
  @import "../../@core/preset/preset/typography/index.scss";

  .page-label {
    @include text-caption-bold();
    color: get-color("subtitle");
    margin-bottom: 12px;
  }

  .page-wrapper {
    margin-top: 16px;
    margin-bottom: -4px;
  }

  .chip-container {
    display: flex;
    flex-wrap: wrap;
    column-gap: 8px;
    row-gap: 6px;
    align-items: center;

    .chip-action-button.v-btn {
      padding: 5px 10px !important;
      height: unset !important;
      min-width: unset;
      color: get-color("secondary-btn") !important;
    }
  }
</style>
